import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { CallApiForOTPVerification, CallSearchPatientsByContactDetails } from "../helpers/http";
import "../styles/Login.css";
import "../styles/ManualLogin.css";
import { IconMail, IconPhone, IconShield, IconBack, IconUserAvatar, IconSignIn } from "../Component/shared/icons/Index";
import { getSessionData } from "../helpers/Util";
import PatientSelection from "./PatientSelection";
import { setSessionStorage } from "../helpers/storage";
import { CheckPatientRegistration } from "./patientRegistration.js/service";
import PatientRegistration from "./patientRegistration.js/PatientRegistration";


const ManualLogin = ({ onBack }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [formData, setFormData] = useState({ email: "", phoneNumber: "" });
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [focused, setFocused] = useState({});
  const [countdown, setCountdown] = useState(0);
  const [isPreFilled, setIsPreFilled] = useState(false);
  const [patientList, setPatientList] = useState([]);
  const [authTokens, setAuthTokens] = useState({});
  const [showPatientSelection, setShowPatientSelection] = useState(false);
  const [showRegisterButton, setShowRegisterButton] = useState(false);
  const [showRegisterPage, setShowRegisterPage] = useState(false);

  const inputRefs = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => { return () => clearInterval(timerRef.current); }, []);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    if (apiError) setApiError("");
  };

  const validateForm = () => {
    const newErrors = {};
    const { email, phoneNumber } = formData;

    if (!email.trim() && !phoneNumber.trim()) {
      newErrors.email = "Please provide an email or phone number";
      newErrors.phoneNumber = "Please provide an email or phone number";
    } else {
      if (email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          newErrors.email = "Please enter a valid email address";
        }
      }
      if (phoneNumber.trim()) {
        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(phoneNumber)) {
          newErrors.phoneNumber = "Please enter a valid 10-digit phone number";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrors({});
    setApiError("");
    if (!validateForm()) return;
    setLoading(true);
    try {
      const checkRegistrationRes = await CheckPatientRegistration(formData);
      if (!checkRegistrationRes?.data?.isRegistered) {
        setApiError("No record registered with the provided contact details. Please register first to continue.");
        setShowRegisterButton(true);
        return;
      }

      const res = await CallSearchPatientsByContactDetails(formData);
      if (res?.success) {
        const data = res?.data.data;
        const accessApiToken = data?.accessApiToken;
        const commonserviceToken = data?.commonserviceToken;
        const rawPatients = data?.patients || [];
        sessionStorage.setItem("allPatientsList", JSON.stringify(rawPatients));
        sessionStorage.setItem("authTokens", JSON.stringify({ accessApiToken, commonserviceToken }));
        setSessionStorage('accessApiToken', accessApiToken);
        setSessionStorage('commonServiceToken', commonserviceToken);

        const uniquePatientsMap = new Map();
        rawPatients.forEach(p => {
          const id = p.patientId || p.PatientId;
          if (id && !uniquePatientsMap.has(id)) {
            uniquePatientsMap.set(id, p);
          } else if (!id) {
            uniquePatientsMap.set(Math.random(), p);
          }
        });
        const patients = Array.from(uniquePatientsMap.values());
        setPatientList(patients);
        setAuthTokens({ accessApiToken, commonserviceToken });

        if (patients.length === 1) {
          const patientData = patients[0];
          const tokenData = {
            ...patientData,
            accessApiToken,
            commonserviceToken,
          };
          // sessionStorage.setItem("tokenData", JSON.stringify({ ...tokenData }));
          setSessionStorage('tokenData', tokenData);
          setSessionStorage('accessApiToken', accessApiToken);
          setSessionStorage('commonserviceToken', commonserviceToken);

          const parseTokenArray = (val) => {
            if (!val) return [];
            if (Array.isArray(val)) return val;
            try { return JSON.parse(val); } catch { return []; }
          };

          sessionStorage.setItem("tokenNodes", JSON.stringify(parseTokenArray(patientData?.nodes || patientData?.Nodes)));
          sessionStorage.setItem("tokenIntakeSheets", JSON.stringify(
            parseTokenArray(
              patientData?.intakeSheets ||
              patientData?.IntakeSheets ||
              patientData?.intakeSheet ||
              patientData?.IntakeSheet
            )
          ));
          sessionStorage.setItem("tokenDocuments", JSON.stringify(
            parseTokenArray(
              patientData?.documents ||
              patientData?.Documents ||
              patientData?.document ||
              patientData?.Document
            )
          ));
          sessionStorage.setItem("tokenRateReviewTemplate", JSON.stringify(
            parseTokenArray(patientData?.rateReviewTemplate || patientData?.RateReviewTemplate)[0] || null
          ));
        }
        setResponse(true);
        startCountdown(40);
      } else {
        setApiError("No matching record found.");
        // toast.error("Patient not found");
      }
    } catch (err) {
      console.error("Error during login:", err);
      const errorMsg = err?.message?.trim() === "No matching patient found." ? 'No matching record found.' : "Something went wrong.";
      setApiError(errorMsg);
      // toast.error(errorMsg);
    } finally {
      setLoading(false);
    }

  };

  const handleChangeOTP = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (apiError) setApiError("");
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0)
      inputRefs.current[index - 1]?.focus();
  };

  const handlePaste = (e, index) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const newOtp = [...otp];
    for (let i = 0; i < pasted.length; i++) {
      if (index + i < 6) newOtp[index + i] = pasted[i];
    }
    setOtp(newOtp);
    inputRefs.current[Math.min(index + pasted.length, 5)]?.focus();
  };

  const handleVerifyOtp = async (e) => {
    const tokenData = getSessionData('tokenData', {})
    e.preventDefault();
    setApiError("");
    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      setApiError('Please enter complete 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const verificationData = {
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        otp: otpValue,
      };
      const res = await CallApiForOTPVerification(verificationData);
      if (res?.success) {
        toast.success(res.message || "OTP verified successfully!");
        sessionStorage.setItem("otpVerified", "true");
        localStorage.setItem("userData", JSON.stringify({
          caseId: formData.caseId,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
        }));
        localStorage.setItem("patientDetails", JSON.stringify({
          DoctorName: formData.doctorName,
          DoctorSpecialtyName: formData.doctorSpecialtyName,
          ProviderName: formData.providerName,
          ...(res.patientDetails || {}),
        }));
        const allPatientsList = getSessionData('allPatientsList', []);
        let IsOnlyPatient = false;

        if (allPatientsList && allPatientsList.length > 0) {
          const uniqueCaseIds = [...new Set(allPatientsList.map(p => p.caseId || p.CaseId).filter(Boolean))];
          IsOnlyPatient = uniqueCaseIds.length === 1;
        }

        // if (tokenData?.isLinkData) {
        //   navigate("/patientPortal");
        // }
        // else {
          if (IsOnlyPatient) {
            navigate("/multiPatientPortal");
          }
          else {
            setShowPatientSelection(true);
          }
        // }
      } else {
        const errorMsg = res?.message || "OTP verification failed";
        setApiError(errorMsg);
        // toast.error(errorMsg);
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      const errorMsg = err?.message || "Invalid OTP. Please try again.";
      setApiError(errorMsg);
      // toast.error(errorMsg);
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    const tokenData = getSessionData('tokenData', {})
    try {
      const loginData = {
        // caseId: formData.caseId,
        // firstName: formData.firstName,
        // lastName: formData.lastName,
        // dateOfBirth: dob,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        // companyId: tokenData.companyId
      };
      const res = await CallSearchPatientsByContactDetails(loginData);
      if (res?.success) {
        setOtp(["", "", "", "", "", ""]);
        toast.success("OTP resent successfully!");
        inputRefs.current[0]?.focus();
        startCountdown(40);
      }
    } catch {
      // toast.error("Failed to resend OTP");
      setApiError("Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const maskEmail = (email) => {
    if (!email || !email.includes("@")) return "";
    const [name, domainFull] = email.split("@");
    const domainParts = domainFull.split(".");
    const extension = domainParts.pop();
    const domain = domainParts.join(".");
    return `${name.slice(0, 3)}***@***${domain.slice(-3)}.${extension}`;
  };

  const startCountdown = (seconds = 40) => {
    clearInterval(timerRef.current);
    setCountdown(seconds);
    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const formatUSPhone = (phone) => {
    if (!phone) return "";
    const digits = phone.replace(/\D/g, "").slice(0, 10);
    if (digits.length < 4) return digits;
    if (digits.length < 7) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const onPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    const digits = pasted.replace(/\D/g, "").slice(0, 10);
    setFormData(prev => ({ ...prev, phoneNumber: digits }));
    if (errors.phoneNumber) setErrors(prev => ({ ...prev, phoneNumber: "" }));
    if (apiError) setApiError("");
  };

  const handleBack = () => {
    sessionStorage.clear();
    setResponse(false);
    setApiError("");
    setOtp(["", "", "", "", "", ""]);
    setCountdown(0);
    clearInterval(timerRef.current);
  };

  const inputClass = (name) => {
    if (errors[name]) return "login-input-error";
    if (focused[name]) return "login-input-focused";
    return "login-input";
  };

  const handlePatientSelect = (patient) => {
    const tokenData = {
      ...patient,
      accessApiToken: authTokens.accessApiToken,
      commonserviceToken: authTokens.commonserviceToken,
    };
    sessionStorage.setItem("tokenData", JSON.stringify({ ...tokenData }));

    const parseTokenArray = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      try { return JSON.parse(val); } catch { return []; }
    };

    sessionStorage.setItem("tokenNodes", JSON.stringify(parseTokenArray(patient?.nodes || patient?.Nodes)));
    sessionStorage.setItem("tokenIntakeSheets", JSON.stringify(
      parseTokenArray(
        patient?.intakeSheets ||
        patient?.IntakeSheets ||
        patient?.intakeSheet ||
        patient?.IntakeSheet
      )
    ));
    sessionStorage.setItem("tokenDocuments", JSON.stringify(
      parseTokenArray(
        patient?.documents ||
        patient?.Documents ||
        patient?.document ||
        patient?.Document
      )
    ));
    sessionStorage.setItem("tokenRateReviewTemplate", JSON.stringify(
      parseTokenArray(patient?.rateReviewTemplate || patient?.RateReviewTemplate)[0] || null
    ));

    navigate("/multiPatientPortal");
  };


  if (showRegisterPage) return <PatientRegistration onSignIn={() => setShowRegisterPage(false)} onBack={onBack ? () => setShowRegisterPage(false) : undefined} />;

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-top-bar" />

        {/* Header */}
        {!response ? (
          <>
            {onBack && (
              <div className="ml-top-back-wrap">
                <button
                  type="button"
                  className="ml-top-back-btn"
                  onClick={onBack}
                >
                  <IconBack /> Back
                </button>
              </div>
            )}

            <div className="manual-login-header-container">
              <div className="login-header-text">
                {isPreFilled && formData.firstName && (
                  <h2 className="login-heading">
                    Welcome! {formData.firstName}
                  </h2>
                )}
                <div className="manual-login-header">
                  <h2 className="login-heading" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px"}}>
                    <IconSignIn width={22} height={22} />
                    Sign In
                  </h2>
                  <p className="login-subheading">
                    {/* We'll send a one-time code to verify it's you. Please enter your registered <strong>EMAIL ADDRESS</strong> or <strong>PHONE NUMBER</strong> to continue. */}
                    We'll send a one-time code to verify your identity. Please enter your registered <strong>EMAIL ADDRESS</strong>, <strong>PHONE NUMBER</strong>, or both to continue.
                  </p>
                </div>
              </div>
            </div>


            <form onSubmit={handleLogin} noValidate>

              {/* <div className="login-dob-notice" style={{ marginBottom: "16px" }}>
                <span className="login-notice-dot" />
                <span>
                  Please enter either mail or phone to continue.
                </span>
              </div> */}

              <div className="login-field-group">
                <label className="login-label">Email Address</label>
                <div className="login-input-wrap">
                  <span className="login-input-icon"><IconMail /></span>
                  <input
                    className={`${inputClass("email")}${isPreFilled ? " login-input-disabled" : ""}`}
                    type="email" name="email" placeholder="Enter your email address"
                    value={formData.email} autoComplete="email" inputMode="email"
                    onChange={handleChange}
                    onFocus={() => setFocused(f => ({ ...f, email: true }))}
                    onBlur={() => setFocused(f => ({ ...f, email: false }))}
                  />
                </div>
                {errors.email && <span className="login-error-text">{errors.email}</span>}
              </div>

              <div className="login-field-group login-field-group--phone">
                <label className="login-label">Phone Number</label>
                <div className="login-input-wrap">
                  <span className="login-input-icon"><IconPhone /></span>
                  <span className="login-phone-prefix">+1</span>
                  <input
                    className={
                      errors.phoneNumber
                        ? "login-input-error login-input-phone-error"
                        : `login-input-phone${isPreFilled ? " login-input-disabled" : ""}`
                    }
                    type="tel" name="phoneNumber" placeholder="10-digit number"
                    value={formatUSPhone(formData.phoneNumber)} inputMode="numeric" autoComplete="tel-national"
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setFormData(prev => ({ ...prev, phoneNumber: digits }));
                      if (errors.phoneNumber) setErrors(prev => ({ ...prev, phoneNumber: "" }));
                      if (apiError) setApiError("");
                    }}
                    onPaste={onPaste}
                    onFocus={() => setFocused(f => ({ ...f, phoneNumber: true }))}
                    onBlur={() => setFocused(f => ({ ...f, phoneNumber: false }))}
                  />
                </div>
                {errors.phoneNumber && <span className="login-error-text">{errors.phoneNumber}</span>}
              </div>

              {apiError && (
                <div className="login-api-error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{apiError}</span>
                </div>
              )}

              <button
                type="submit"
                className={`login-btn-primary${loading || (!formData.email.trim() && !formData.phoneNumber.trim()) ? " login-btn-primary--disabled" : ""}`}
                disabled={loading || (!formData.email.trim() && !formData.phoneNumber.trim())}
              >
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)"></circle>
                      <path d="M12 2a10 10 0 0 1 10 10" stroke="#fff">
                        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
                      </path>
                    </svg>
                    Verifying contact details...
                  </span>
                ) : "Proceed to OTP Verification →"}
              </button>
            </form>

            <div className="ml-register-row">
              <span className="ml-register-text">New user?</span>
              <button
                type="button"
                className="ml-register-btn"
                onClick={() => {
                  setShowRegisterPage(true);
                  setApiError("");
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <line x1="20" y1="8" x2="20" y2="14" />
                  <line x1="23" y1="11" x2="17" y2="11" />
                </svg>
                Register here
              </button>
            </div>

            <p className="login-footer"><IconShield />Your information is encrypted and secure.</p>
          </>
        ) : showPatientSelection ? (
          <PatientSelection patients={patientList} onSelect={handlePatientSelect} />
        ) : (
          <>
            <div className="login-otp-icon-wrap">
              <div className="login-otp-icon-circle">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2d8a5e" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <polyline points="9 12 11 14 15 10" />
                </svg>
              </div>
            </div>

            <h2 className="login-otp-heading">Verify it's you, {formData.firstName}</h2>
            <p className="login-otp-sub">A 6-digit OTP has been sent to</p>

            <div className="login-otp-contact-cards">
              {formData.phoneNumber && formData.phoneNumber.trim() !== "" && (
                <div className="login-otp-contact-card">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2d8a5e" strokeWidth="2">
                    <rect x="5" y="2" width="14" height="20" rx="2" />
                    <line x1="12" y1="18" x2="12.01" y2="18" />
                  </svg>
                  <span className="login-otp-contact-key">Mobile</span>
                  <span className="login-otp-contact-val">+1 ••••••{formData.phoneNumber.slice(-4)}</span>
                </div>
              )}
              {formData.email && formData.email.trim() !== "" && (
                <div className="login-otp-contact-card">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2d8a5e" strokeWidth="2">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <polyline points="2,4 12,13 22,4" />
                  </svg>
                  <span className="login-otp-contact-key">Email</span>
                  <span className="login-otp-contact-val">{maskEmail(formData.email)}</span>
                </div>
              )}
            </div>

            <form onSubmit={handleVerifyOtp}>
              <p className="login-otp-code-label">Enter your 6-digit verification code</p>
              <div className="login-otp-row">
                {otp.map((digit, index) => (
                  <React.Fragment key={index}>
                    {/* {index === 3 && <span className="login-otp-dash"></span>} */}
                    <input
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text" inputMode="numeric"
                      autoComplete={index === 0 ? "one-time-code" : "off"}
                      maxLength={1} value={digit}
                      className={digit ? "login-otp-box--filled" : "login-otp-box"}
                      onChange={(e) => handleChangeOTP(e.target.value, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      onPaste={(e) => handlePaste(e, index)}
                    />
                  </React.Fragment>
                ))}
              </div>
              <div className="login-resend-row">
                {countdown > 0 ? (
                  <span>Resend code in <span className="login-resend-timer">0:{String(countdown).padStart(2, "0")}</span></span>
                ) : (
                  <button type="button" className="login-resend-btn" onClick={handleResendOTP} disabled={loading}>
                    Resend OTP
                  </button>
                )}
              </div>

              {apiError && (
                <div className="login-api-error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{apiError}</span>
                </div>
              )}

              <button
                type="submit"
                className={`login-btn-primary${loading || otp.join("").length !== 6 ? " login-btn-primary--disabled" : ""}`}
                disabled={loading || otp.join("").length !== 6}
              >
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)"></circle>
                      <path d="M12 2a10 10 0 0 1 10 10" stroke="#fff">
                        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
                      </path>
                    </svg>
                    Verifying...
                  </span>
                ) : "Verify OTP"}
              </button>
            </form>

            <div className="login-back-btn-wrap">
              <button className="login-back-btn" onClick={handleBack}>
                <IconBack /> Back to Login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ManualLogin;